import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBhajanDataRequest, selectCatalog, selectStatus, selectError } from '../Features/BhajanSlice';
import { AppDispatch } from '../store/store';
import { useNavigate } from 'react-router-dom';
import { FilterBar } from '../filters/components/FilterBar';
import { useFilterSet } from '../filters/hooks/useFilterSet';
import { useFilterUrl } from '../filters/hooks/useFilterUrl';
import { useFilteredNodes } from '../filters/hooks/useFilteredNodes';
import { makeBhajanFilterDefinitions } from '../browse/bhajanFilterDefinitions';
import { orderResults } from '../browse/resultOrdering';
import { projectCatalogNodes, type CatalogNode } from '../browse/projectCatalogNodes';
import type { AnyGraphNode } from '../types/graphNodes';
import { NodeTypeId } from '../types/graphNodes';
import { APP } from '../app/appConfig';
import { inAppDomain } from '../app/appPolicy';
import { BhajanGrid } from './BhajanGrid';
import { bhajanToCardData } from './BhajanCard';

interface BhajansProps {
  language: string;
}

const Bhajans: React.FC<BhajansProps> = ({ language }) => {
  const dispatch = useDispatch<AppDispatch>();
  const catalog = useSelector(selectCatalog);
  const status = useSelector(selectStatus);
  const errorMsg = useSelector(selectError);
  const navigate = useNavigate();

  const loading = status === 'loading' || (status === 'idle' && !catalog);
  const error = status === 'failed' ? (errorMsg ?? 'Could not load bhajans.') : null;

  // Filter state lives in useFilterSet; useFilterUrl syncs both directions
  // with the URL so links are shareable + the back-button restores filters.
  const { activeFilters, setFilters } = useFilterSet();
  useFilterUrl(activeFilters, setFilters);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchBhajanDataRequest());
  }, [dispatch, status]);

  const allNodes: AnyGraphNode[] = useMemo(
    () => projectCatalogNodes((catalog as { nodes?: CatalogNode[] } | null)?.nodes),
    [catalog]
  );

  /**
   * Bhajan browser is hard-scoped to the running app's domain. We pre-narrow
   * here instead of exposing a domain facet — the public site has no need
   * to look at the other apps' content.
   */
  const bhajanNodes: AnyGraphNode[] = useMemo(
    () => allNodes.filter((n) => n.nodeTypeId === NodeTypeId.BHAJAN && inAppDomain(n)),
    [allNodes]
  );

  const definitions = useMemo(() => makeBhajanFilterDefinitions(allNodes), [allNodes]);

  const filterResult = useFilteredNodes(bhajanNodes, activeFilters, definitions);

  // Alphabetical by displayed title, except while an affirmative name search
  // ("sounds like", contains, …) is active — then relevance order (Fuse
  // text-then-phonetic, matching jnana-native) wins.
  const orderedNodes = useMemo(
    () => orderResults(filterResult.nodes, activeFilters),
    [filterResult.nodes, activeFilters]
  );

  const cardData = useMemo(
    () => orderedNodes.map((n) => bhajanToCardData(n as unknown as AnyGraphNode, allNodes)),
    [orderedNodes, allNodes]
  );

  return (
    <main id="main">
      <section
        aria-label="Bhajan browser"
        className="relative border-b border-light"
        style={{ background: APP.browseHeroBackground }}
      >
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12 text-center">
          <h1 className="text-h1 font-display font-semibold mb-2">Bhajans</h1>
          <p className="text-text-secondary mb-6">
            {loading
              ? 'Loading the library…'
              : `${filterResult.filteredCount.toLocaleString()} of ${filterResult.totalCount.toLocaleString()} bhajans`}
          </p>
          <div className="max-w-2xl mx-auto">
            <FilterBar
              filterSet={activeFilters}
              onFilterSetChange={setFilters}
              nodes={bhajanNodes}
              definitions={definitions}
              availableFilters={['name', 'nodeId']}
              placeholder="Search bhajans by name…"
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        {error ? (
          <div className="rounded-2xl border border-accent-rose/40 bg-accent-rose/10 px-6 py-8 text-center">
            <p className="text-accent-rose font-semibold mb-2">Couldn't load bhajans.</p>
            <p className="text-text-secondary">{error}</p>
          </div>
        ) : (
          <BhajanGrid
            bhajans={cardData}
            language={language}
            density="comfortable"
            isLoading={loading}
            onSelect={(b) => navigate(`/nodes/${b.nodeId}`)}
          />
        )}
      </div>
    </main>
  );
};

export default Bhajans;
